import { yup } from '@framework/validation/initYup.ts';
import { ReferenceSchema } from '../../validation/ReferenceSchema.ts';
import {
  type ComputeExpression,
  type IComputeExpressionValueReference,
  type IComputeExpressionValueText,
} from '../ComputeExpression.ts';
import { ComputeMath } from '../ComputeMath.ts';

const ComputeExpressionValueItemSchema = yup
  .object({
    type: yup
      .string()
      .oneOf(['text', 'compute-expression-reference'])
      .required(),
    text: yup.string().notRequired(),
    attrs: yup
      .object({
        id: yup.string().notRequired(),
        label: yup.string().notRequired(),
        reference: ReferenceSchema.notRequired(),
      })
      .notRequired(),
  })
  .noUnknown()
  .required();

const ComputeExpressionRawSchema = yup.string().default('').required();

const ComputeExpressionBaseSchema: yup.ObjectSchema<ComputeExpression> =
  yup.object({
    value: yup
      .array(
        ComputeExpressionValueItemSchema as yup.ObjectSchema<
          IComputeExpressionValueReference | IComputeExpressionValueText
        >,
      )
      .default([])
      .min(1, "L'espressione non può essere vuota")
      .label('Espressione')
      .required(),
    raw: ComputeExpressionRawSchema,
  });

/**
 * Crea lo schema di validazione per un'espressione di calcolo
 * di tipo `ComputeExpression`.
 */
export const ComputeExpressionSchema: yup.ObjectSchema<ComputeExpression> =
  ComputeExpressionBaseSchema.shape({
    raw: ComputeExpressionRawSchema
      // Se il value non è definito, non è richiesto
      // lasciamo che l'errore sia lanciato solo dal value in `ComputeExpressionBaseSchema`
      // in questo modo, non mostriamo due errori nel formItem(che per l'utente non hanno senso)
      .when(['value'], (value, schema) => {
        const computeExpressionValue = value[0] as any;
        // Se il value non è definito
        if (
          !computeExpressionValue ||
          (Array.isArray(computeExpressionValue) &&
            computeExpressionValue.length === 0)
        ) {
          return schema.notRequired();
        }
        // Le expression numeriche richiedono un parser per essere validate
        return schema.test({
          message: "Il campo ${path} deve essere un'espressione valida",
          name: 'validComputeExpression',
          exclusive: true,
          test(value) {
            // Se non c'è value o non è una stringa, la validazione passa e lasciamo che sia
            // il required a gestire l'errore
            if (!value || typeof value !== 'string') return true;
            try {
              const parsed = ComputeMath.parse(value.replace(/@/g, ''));
              return true;
            } catch (e) {
              if (
                e != null &&
                typeof e === 'object' &&
                'char' in e &&
                typeof e.char === 'number'
              ) {
                return this.createError({
                  path: this.path,
                  message: `L'espressione non è valida, è presente un errore alla posizione ${e.char}.`,
                });
              }
              return false;
            }
          },
        });
      })
      // Mettiamo una label per facilitare la comprensione del campo
      // e il debugging, ma non dovrebbe venire mostrata all'utente
      // a meno di comportamenti anomali.
      .label('Espressione raw'),
  });

/**
 * Schema di validazione per un'espressione di calcolo di tipo `ComputeExpression`
 * di tipo stringa. In questo caso non andiamo a validare l'espressione con il parser
 * perché i caratteri speciali delle stringhe potrebbero generare errori.
 */
export const ComputeStringExpressionSchema: yup.ObjectSchema<ComputeExpression> =
  ComputeExpressionBaseSchema;
